<template>
  <div class="step">
    <div class="step-header">
      <div class="step-icon">
        <v-icon size="24" color="#e8612f">mdi-briefcase-outline</v-icon>
      </div>
      <div class="step-content">
        <h2 class="title">General</h2>
        <p class="desc">Choose your business type and primary service type.</p>
      </div>
    </div>

    <v-form class="form">
      <AppTextField
        v-model="businessEmail"
        type="email"
        label="Business Email"
        label-icon="mdi-email-outline"
        placeholder="name@company.com"
        :error-messages="businessEmailErrors"
      />

      <AppTextField
        v-model="businessPhone"
        type="tel"
        label="Business Phone"
        v-mask="'(###) ###-####'"
        label-icon="mdi-phone-outline"
        placeholder="(555) 555-5555"
        :error-messages="businessPhoneErrors"
      />

      <AppSelectField
        v-model="businessType"
        :items="businessTypeOptions"
        item-title="label"
        item-value="value"
        label="Business Type"
        label-icon="mdi-briefcase-outline"
        placeholder="Select business type"
        :error-messages="businessTypeErrors"
      />

      <AppSelectField
        v-model="serviceType"
        :items="serviceTypeOptions"
        item-title="label"
        item-value="value"
        label="Service Type"
        label-icon="mdi-shape-outline"
        placeholder="Select service type"
        :loading="isLoadingServiceTypes"
        :disabled="isLoadingServiceTypes"
        :error-messages="serviceTypeErrors"
      />

      <AppTextField
        v-model="taxId"
        label="Tax ID"
        label-icon="mdi-domain"
        placeholder="12-3456789"
        :error-messages="taxIdErrors"
      />
    </v-form>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import AppSelectField from "@/shared/components/ui/AppSelectField.vue";
import AppTextField from "@/shared/components/ui/AppTextField.vue";
import { fetchServiceGroups } from "@/shared/services/vendor-requests.api";

const props = defineProps<{
  current_step?: number;
  errors?: Record<string, any> | null;
  profileInfo?: any;
}>();

const businessType = ref("");
const serviceType = ref("");
const businessEmail = ref("");
const businessPhone = ref("");
const taxId = ref("");
const isLoadingServiceTypes = ref(false);

const businessTypeOptions = [
  { label: "Sole Proprietorship", value: "sole_proprietorship" },
  { label: "Partnership", value: "partnership" },
  { label: "LLC", value: "llc" },
  { label: "Corporation", value: "corporation" },
  { label: "Freelancer", value: "freelancer" },
];

const serviceTypeOptions = ref([
  { label: "Pest Control", value: "pest_control" },
  { label: "Cleaning", value: "cleaning" },
  { label: "Plumbing", value: "plumbing" },
  { label: "Electrical", value: "electrical" },
  { label: "HVAC", value: "hvac" },
  { label: "Landscaping", value: "landscaping" },
]);

watch(
  () => props.profileInfo,
  (info) => {
    if (!info) return;
    const businessValue =
      info?.general?.business_type ||
      info?.business_type ||
      info?.business_info?.business_type ||
      info?.vendor?.business_type ||
      "";
    const serviceValue =
      info?.general?.service_category ||
      info?.service_category ||
      info?.service_type ||
      info?.business_info?.service_category ||
      info?.vendor?.service_category ||
      "";
    const businessEmailValue =
      info?.general?.business_email ||
      info?.business_email ||
      info?.business_info?.business_email ||
      info?.vendor?.business_email ||
      info?.vendor?.email ||
      "";
    const businessPhoneValue =
      info?.general?.business_phone ||
      info?.business_phone ||
      info?.business_info?.business_phone ||
      info?.vendor?.business_phone ||
      info?.vendor?.phone ||
      "";
    const taxIdValue =
      info?.general?.tax_id ||
      info?.tax_id ||
      info?.business_info?.tax_id ||
      info?.vendor?.tax_id ||
      info?.vendor?.pan_no ||
      "";

    if (!businessType.value && businessValue) businessType.value = String(businessValue);
    if (!serviceType.value && serviceValue) serviceType.value = String(serviceValue);
    if (!businessEmail.value && businessEmailValue) businessEmail.value = String(businessEmailValue);
    if (!businessPhone.value && businessPhoneValue) businessPhone.value = String(businessPhoneValue);
    if (!taxId.value && taxIdValue) taxId.value = String(taxIdValue);
  },
  { immediate: true },
);

onMounted(async () => {
  isLoadingServiceTypes.value = true;
  try {
    const response = await fetchServiceGroups();
    const list = Array.isArray(response?.data) ? response.data : [];
    if (list.length) {
      serviceTypeOptions.value = list.map((group: any) => ({
        label: group.label || group.name || group.title || "Service",
        value: group.slug || group.code || group.id,
      }));
    }
  } catch (error) {
    // Keep fallback options.
  } finally {
    isLoadingServiceTypes.value = false;
  }
});

function getErrorsForField(field: string): string[] {
  const errs: any = props.errors;
  if (!errs) return [];

  const root = errs?.errors && typeof errs.errors === "object" ? errs.errors : errs;
  const raw = root?.[field];
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.map((v) => String(v));
  return [String(raw)];
}

const businessTypeErrors = computed(() => getErrorsForField("business_type"));
const serviceTypeErrors = computed(() => getErrorsForField("service_category"));
const businessEmailErrors = computed(() => getErrorsForField("business_email"));
const businessPhoneErrors = computed(() => getErrorsForField("business_phone"));
const taxIdErrors = computed(() => getErrorsForField("tax_id"));

function getValues() {
  return {
    business_email: businessEmail.value,
    business_phone: businessPhone.value,
    business_type: businessType.value,
    service_category: serviceType.value,
    tax_id: taxId.value,
  };
}

defineExpose({ getValues });
</script>

<style scoped>
.step {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.step-header {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 8px;
}

.step-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: var(--radius-lg);
  background: rgba(232, 97, 47, 0.1);
  flex-shrink: 0;
}

.step-content {
  flex: 1;
}

.title {
  font-size: 16px;
  font-weight: 600;
  line-height: 1.2;
  margin-bottom: 4px;
  letter-spacing: -0.025em;
}

.desc {
  margin: 0;
  color: var(--color-subtitle-light);
  font-size: 14px;
  line-height: 1.2;
  font-weight: 400;
}

.form {
  display: grid;
  gap: 20px;
  margin-top: 8px;
}
</style>
